/**
 * Leaf → PostgREST filter translation (#113).
 *
 * The console's saved views carry a `Leaf` filter tree that was, until now,
 * evaluated in the BROWSER against whatever page had been fetched. On a table
 * larger than the page size that produces confidently wrong answers: measured on
 * 204,560 invoices, a `status = paid` chip reported 125 matches against a true
 * 50,022, and a sort-by-value view topped out below the actual maximum, so the
 * biggest record was simply absent from a screen whose whole purpose was to
 * show it.
 *
 * ── The rule this module exists to enforce ────────────────────────────────
 * **Translate faithfully or refuse.** The JS evaluator and PostgREST do NOT
 * agree on every operator:
 *
 *   • `eq` in JS compares `String(a).toLowerCase() === String(b).toLowerCase()`.
 *     PostgREST's `eq.` is case-SENSITIVE. Mapping one to the other would change
 *     which rows match.
 *   • `empty` in JS is `v == null || v === ''`. `is.null` does not cover the
 *     empty string.
 *   • `contains` in JS is a case-insensitive substring test, which `ilike.*v*`
 *     does match — but only once the value is escaped.
 *
 * Where a faithful translation exists, this returns it. Where one does not, it
 * returns `null` and the caller keeps evaluating that view client-side. Swapping
 * one wrong answer for a differently wrong answer is not a fix, and a filter that
 * quietly changes meaning between the browser and the database is worse than one
 * that is honestly slow.
 *
 * ── The FIELD's kind decides the operator's SQL (#959 column filters) ──────
 * `ilike` is a TEXT operator. `numeric ILIKE text` does not exist in Postgres,
 * and neither does `uuid ~~* text` or `date ~~* text` — PostgREST answers 400
 * and the list shows an error where it used to show rows. That was latent for as
 * long as the only author of a filter leaf was a migration writing a registered
 * view; the column ⋮ menu makes "Amount = 100" one click away, so the
 * translation now takes the field's `core.field_meta.kind` and picks the
 * operator family the column's TYPE can actually run:
 *
 *   text · email · phone · country · option   →  the ilike family (unchanged)
 *   number · money · percent · date · datetime · boolean · ref · uuid  →  exact
 *   kind not given                            →  unchanged, byte for byte
 *
 * Both families stay faithful to `evalLeaf`, which is the only thing that
 * matters: on a non-text column `String(v).toLowerCase() === String(val).toLowerCase()`
 * and `eq.` agree, because a number has no case. On a TEXT column they do not,
 * which is why text keeps `ilike`. Substring operators are refused outright off
 * text, because there is no faithful answer to "does this uuid contain 'ab'"
 * that a user asking it would recognise.
 */

/** What `core.field_meta.kind` says a column IS. Anything else ⇒ treat as unknown. */
export type FieldKind =
  | 'text' | 'email' | 'phone' | 'country' | 'option'
  | 'number' | 'money' | 'percent' | 'date' | 'datetime' | 'boolean' | 'ref' | 'uuid' | 'json'

/** Kinds whose column really is text, so `ilike` runs and case-insensitivity is the honest reading. */
const TEXTUAL: ReadonlySet<string> = new Set(['text', 'email', 'phone', 'country', 'option'])

/** Resolve a leaf's field to its declared kind. Absent ⇒ the pre-#959 behaviour. */
export interface TranslateOptions { kindOf?: (field: string) => string | undefined }

export interface FilterLeaf {
  field?: string
  op?: string
  value?: unknown
  all?: FilterLeaf[]
  any?: FilterLeaf[]
  not?: FilterLeaf
}

/** One `key=value` pair for a PostgREST query string. */
export interface PgFilter { key: string; value: string }

/** PostgREST reserves , . : ( ) inside filter values; quote when present. */
function lit(v: unknown): string {
  const s = String(v ?? '')
  return /[,.:()"\s]/.test(s) ? `"${s.replace(/"/g, '\\"')}"` : s
}

/** Escape LIKE wildcards so a literal % or _ in the needle stays literal. */
function likeNeedle(v: unknown): string {
  return String(v ?? '').replace(/([%_])/g, '\\$1')
}

/**
 * Translate ONE leaf. Returns null when no faithful equivalent exists — the
 * caller must then fall back rather than approximate.
 *
 * `kind` is the field's `core.field_meta.kind`. Omit it and the translation is
 * exactly what it was before #959 — every existing caller is unchanged.
 */
export function leafToPostgrest(leaf: FilterLeaf, kind?: string): PgFilter | null {
  // Boolean composites: PostgREST expresses these as or=(…) / and=(…), but the
  // nesting and quoting rules differ enough from this tree that translating them
  // half-correctly is exactly the risk this module exists to avoid. Refused for
  // now — a composite view stays client-side and stays correct.
  if (leaf.all || leaf.any || leaf.not) return null
  const { field, op, value } = leaf
  if (!field || !op) return null
  // a $-bind ($me, $record) is resolved from context the server does not have
  if (typeof value === 'string' && value.startsWith('$')) return null

  // A column whose kind is DECLARED and is not textual cannot run `ilike` at all
  // (`numeric ~~* unknown` does not exist). Where the kind is not declared the
  // pre-#959 behaviour stands, so nothing that worked stops working.
  const textual = kind === undefined || TEXTUAL.has(kind)

  switch (op) {
    // On TEXT, NOT `eq.`: the JS evaluator lowercases both sides, so a faithful
    // translation of its semantics is a case-insensitive match. `ilike` with no
    // wildcards is exactly that. It forfeits a plain btree index — the honest
    // trade for not changing which rows come back.
    //
    // Off text there is no case to be insensitive about — `String(100)` and
    // `String('100')` lowercase to the same thing — so `eq.` IS the faithful
    // translation, and it is the only one the column's type can execute.
    case 'eq': return textual
      ? { key: field, value: `ilike.${lit(likeNeedle(value))}` }
      : { key: field, value: `eq.${lit(value)}` }
    case 'ne': return textual
      ? { key: field, value: `not.ilike.${lit(likeNeedle(value))}` }
      : { key: field, value: `neq.${lit(value)}` }
    // Substring operators are TEXT operators. Asked of a uuid or a numeric they
    // have no faithful answer, so they are refused rather than approximated.
    case 'contains': return textual ? { key: field, value: `ilike.*${likeNeedle(value)}*` } : null
    case 'starts': return textual ? { key: field, value: `ilike.${likeNeedle(value)}*` } : null
    case 'ends': return textual ? { key: field, value: `ilike.*${likeNeedle(value)}` } : null
    case 'gt': return { key: field, value: `gt.${lit(value)}` }
    case 'gte': return { key: field, value: `gte.${lit(value)}` }
    case 'lt': return { key: field, value: `lt.${lit(value)}` }
    case 'lte': return { key: field, value: `lte.${lit(value)}` }
    case 'after': return { key: field, value: `gt.${lit(value)}` }
    case 'before': return { key: field, value: `lt.${lit(value)}` }
    case 'in':
      if (!Array.isArray(value)) return null
      return { key: field, value: `in.(${value.map(lit).join(',')})` }
    // `empty` means null OR '' in JS. `is.null` alone would miss the empty
    // string, and expressing the disjunction needs an or=() composite — refused
    // above. Left client-side deliberately.
    case 'empty': return null
    default: return null
  }
}

/**
 * Translate a whole filter list. **All or nothing**: if any leaf cannot be
 * translated faithfully the caller must evaluate the entire view client-side,
 * because a partially-pushed filter returns a set that is neither the server's
 * answer nor the browser's.
 */
export function filtersToPostgrest(leaves: FilterLeaf[], opts?: TranslateOptions): PgFilter[] | null {
  const out: PgFilter[] = []
  for (const leaf of leaves) {
    const f = leafToPostgrest(leaf, leaf.field ? opts?.kindOf?.(leaf.field) : undefined)
    if (!f) return null
    out.push(f)
  }
  return out
}

export type SortDirection = 'asc' | 'desc'
export interface SortTerm { key: string; direction: SortDirection }

/**
 * Parse the saved-view sort grammar.
 *
 * The original grammar was one flat pair, `['amount', 'desc']`. Extending the
 * SAME array with more pairs keeps every persisted view and export valid:
 * `['status', 'asc', 'amount', 'desc']`. Invalid/incomplete pairs are ignored,
 * and a repeated key keeps its first (highest-priority) occurrence.
 */
export function parseSortSpec(sortSpec: unknown[] | undefined): SortTerm[] {
  const out: SortTerm[] = []
  const seen = new Set<string>()
  const spec = sortSpec ?? []
  for (let i = 0; i < spec.length; i += 2) {
    const key = spec[i]
    if (typeof key !== 'string' || key.length === 0 || seen.has(key)) continue
    const rawDirection = spec[i + 1]
    out.push({ key, direction: rawDirection === 'desc' ? 'desc' : 'asc' })
    seen.add(key)
  }
  return out
}

/** Encode terms back into the backwards-compatible flat saved-view grammar. */
export function encodeSortSpec(terms: SortTerm[]): string[] {
  return terms.flatMap(({ key, direction }) => [key, direction])
}

/**
 * `['amount','desc']` → `amount.desc.nullslast`; additional pairs become a
 * comma-separated PostgREST order, preserving priority and stable paging.
 */
export function sortToPostgrest(sortSpec: unknown[] | undefined): string | null {
  const terms = parseSortSpec(sortSpec)
  if (terms.length === 0) return null
  return terms.map(({ key, direction }) => `${key}.${direction}.nullslast`).join(',')
}

/* ── Search and group conditions (#2375, ADR F-84) ──────────────────────────
 * Not a translation of a saved-view leaf — these are the two server questions
 * the unified grid asks that a leaf cannot: "does any searchable column contain
 * this text?" and "which rows belong to this group?". Both run under the
 * caller's own RLS through the ordinary read, so they cannot widen what a
 * person sees; they only narrow it.
 */

/** Quote a value inside a PostgREST logic tree (`or=(…)`/`and=(…)`). */
function treeLit(v: string): string {
  // `*` is PostgREST's like-wildcard and is NOT reserved, so it never forces quotes.
  // Inside quotes a backslash is an escape, so the LIKE escape `\%` is doubled.
  return /[,.:()"\s]/.test(v) ? `"${v.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"` : v
}

/**
 * Free-text search over the given TEXTUAL columns: a case-insensitive substring
 * of ANY of them — the body of an `or` group, or null when there is nothing to
 * ask. Non-text columns must not be passed: `ilike` does not exist on them, and
 * a reference column's value is a uuid nobody searches by (its LABEL is not in
 * the row, so a server search cannot see it — the host says so).
 */
export function searchCondition(q: string, columns: readonly string[]): string | null {
  const needle = q.trim()
  if (!needle || columns.length === 0) return null
  const pattern = treeLit(`*${likeNeedle(needle)}*`)
  return columns.map((column) => `${column}.ilike.${pattern}`).join(',')
}

/** Rows whose group column is exactly `value` (not the case-folded `eq` of a leaf). */
export function groupValueFilter(column: string, value: string | boolean): PgFilter {
  return { key: column, value: `eq.${typeof value === 'boolean' ? String(value) : lit(value)}` }
}

/**
 * Rows with NO value in the group column. On a text column the empty string is
 * missing too — as one group, "Not provided" — so it is an `or` group there;
 * elsewhere it is `is.null`.
 */
export function groupMissingCondition(column: string, textual: boolean): { filters: PgFilter[]; orGroup: string | null } {
  return textual
    ? { filters: [], orGroup: `${column}.is.null,${column}.eq.""` }
    : { filters: [{ key: column, value: 'is.null' }], orGroup: null }
}

/** Rows with a value OUTSIDE the declared set — the "Other values" group. */
export function groupOtherFilters(column: string, declared: readonly (string | boolean)[], textual: boolean): PgFilter[] {
  const out: PgFilter[] = [{ key: column, value: 'not.is.null' }]
  if (textual) out.push({ key: column, value: 'neq.""' })
  if (declared.length > 0) out.push({ key: column, value: `not.in.(${declared.map((v) => typeof v === 'boolean' ? String(v) : lit(v)).join(',')})` })
  return out
}

/**
 * Several `or` groups ANDed together, as ONE parameter. Search and the missing
 * group are both `or` groups; combining them explicitly keeps the meaning
 * independent of how the server treats a repeated `or` key.
 */
export function combineOrGroups(groups: readonly (string | null | undefined)[]): PgFilter[] {
  const live = groups.filter((group): group is string => !!group)
  if (live.length === 0) return []
  if (live.length === 1) return [{ key: 'or', value: `(${live[0]})` }]
  return [{ key: 'and', value: `(${live.map((group) => `or(${group})`).join(',')})` }]
}
